import React, { useState } from "react";
import { Eye, EyeOff, Target } from "lucide-react";
import { useAnalytics } from "../../providers/AnalyticsProvider";
import MagicLinkLogin from "./MagicLinkLogin";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showMagicLink, setShowMagicLink] = useState(false);
  const analytics = useAnalytics();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Form validation
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    if (!password) {
      setError("Please enter your password");
      return;
    }

    // Track login attempt
    analytics.trackUserAction('login_attempt', 'login_page');

    // Show loading state
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      if (email.includes("@") && password.length >= 6) {
        analytics.trackConversion('login_success', { method: 'email' });
        // Redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        setError("Invalid email or password. Please try again.");
        analytics.trackUserAction('login_failed', 'login_page');
      }
    }, 1500);
  };


  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(ellipse at top, #1e293b 0%, #0f172a 50%, #020617 100%)",
      color: "white",
      fontFamily: "system-ui, -apple-system, sans-serif",
      display: "flex",
      flexDirection: "column"
    }}>
      {/* Header */}
      <header style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        backdropFilter: "blur(16px)",
        backgroundColor: "rgba(30, 41, 59, 0.8)",
        borderBottom: "1px solid rgba(71, 85, 105, 0.3)"
      }}>
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 16px"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            height: "64px"
          }}>
            <a href="/" style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              textDecoration: "none",
              color: "inherit"
            }}>
              <div style={{
                height: "32px",
                width: "32px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <Target className="h-5 w-5 text-white" />
              </div>
              <div style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: "1.1"
              }}>
                <span style={{ fontWeight: "bold", fontSize: "20px" }}>BriefcaseAI</span>
                <span style={{ fontSize: "14px", fontWeight: "normal", opacity: "0.8" }}>Telemetry Dashboard</span>
              </div>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px"
      }}>
        <div style={{
          width: "100%",
          maxWidth: "400px"
        }}>
          {/* Welcome Content */}
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <h1 style={{
              fontSize: "32px",
              fontWeight: "bold",
              marginBottom: "16px"
            }}>Welcome back</h1>
            <p style={{
              color: "rgb(148, 163, 184)",
              marginBottom: "24px"
            }}>
              Monitor, optimize, and secure your AI systems with comprehensive telemetry
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "20px" }}>📊</span>
                <p style={{ color: "rgb(148, 163, 184)", textAlign: "left", margin: 0 }}>
                  Real-time AI performance monitoring
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "20px" }}>⚡</span>
                <p style={{ color: "rgb(148, 163, 184)", textAlign: "left", margin: 0 }}>
                  Intelligent optimization recommendations
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "20px" }}>🛡️</span>
                <p style={{ color: "rgb(148, 163, 184)", textAlign: "left", margin: 0 }}>
                  Enterprise security & compliance
                </p>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <div style={{
            backdropFilter: "blur(8px)",
            background: "rgba(30, 41, 59, 0.5)",
            border: "1px solid rgba(71, 85, 105, 0.5)",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
          }}>
            <h2 style={{
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "24px",
              textAlign: "center"
            }}>Sign in to your account</h2>

            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: "16px",
                padding: "12px",
                borderRadius: "8px",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)"
              }}>
                <p style={{ color: "#f87171", fontSize: "14px", textAlign: "center", margin: 0 }}>
                  {error}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {!showMagicLink && (
                <>
                  {/* Email Field */}
                  <div style={{ marginBottom: "16px" }}>
                    <label htmlFor="email" style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "4px"
                    }}>
                      Email address
                    </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "rgb(15, 23, 42)",
                    border: "1px solid rgb(71, 85, 105)",
                    borderRadius: "6px",
                    color: "white",
                    fontSize: "16px",
                    outline: "none"
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                  onBlur={(e) => e.target.style.borderColor = "rgb(71, 85, 105)"}
                  disabled={isLoading}
                />
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: "16px" }}>
                <label htmlFor="password" style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "4px"
                }}>
                  Password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    style={{
                      width: "100%",
                      padding: "12px",
                      paddingRight: "48px",
                      background: "rgb(15, 23, 42)",
                      border: "1px solid rgb(71, 85, 105)",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "16px",
                      outline: "none"
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                    onBlur={(e) => e.target.style.borderColor = "rgb(71, 85, 105)"}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      color: "rgb(148, 163, 184)",
                      cursor: "pointer"
                    }}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "24px"
              }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    style={{
                      width: "16px",
                      height: "16px",
                      marginRight: "8px"
                    }}
                    disabled={isLoading}
                  />
                  <label htmlFor="remember" style={{
                    fontSize: "14px",
                    color: "rgb(148, 163, 184)"
                  }}>
                    Remember me for 30 days
                  </label>
                </div>
                <a href="/forgot-password" style={{
                  fontSize: "14px",
                  color: "#60a5fa",
                  textDecoration: "none"
                }}>
                  Forgot password?
                </a>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "6px",
                  background: isLoading ? "#6b7280" : "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  border: "none",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s"
                }}
                disabled={isLoading}
              >
                {isLoading && (
                  <div style={{
                    width: "20px",
                    height: "20px",
                    border: "2px solid white",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "8px"
                  }} />
                )}
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
                </>
              )}

              {/* Divider */}
              <div style={{
                display: "flex",
                alignItems: "center",
                margin: "24px 0"
              }}>
                <div style={{
                  flex: 1,
                  height: "1px",
                  background: "rgb(71, 85, 105)"
                }} />
                <span style={{
                  padding: "0 16px",
                  fontSize: "14px",
                  color: "rgb(148, 163, 184)"
                }}>Or continue with</span>
                <div style={{
                  flex: 1,
                  height: "1px",
                  background: "rgb(71, 85, 105)"
                }} />
              </div>

              {/* Magic Link Option */}
              <button
                type="button"
                onClick={() => setShowMagicLink(!showMagicLink)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                  border: "1px solid rgb(71, 85, 105)",
                  borderRadius: "6px",
                  background: showMagicLink ? "rgba(71, 85, 105, 0.3)" : "transparent",
                  color: "white",
                  fontSize: "14px",
                  cursor: "pointer",
                  transition: "background 0.3s"
                }}
                onMouseEnter={(e) => !showMagicLink && ((e.target as HTMLElement).style.background = "rgba(71, 85, 105, 0.3)")}
                onMouseLeave={(e) => !showMagicLink && ((e.target as HTMLElement).style.background = "transparent")}
                disabled={isLoading}
              >
                <span style={{ marginRight: "8px" }}>✨</span>
                {showMagicLink ? "Use Password Instead" : "Sign in with Magic Link"}
              </button>

              {/* Magic Link Component */}
              {showMagicLink && (
                <div style={{ marginTop: "16px" }}>
                  <MagicLinkLogin
                    onSuccess={() => window.location.href = '/dashboard'}
                    onError={(error) => setError(error)}
                    standalone={false}
                  />
                </div>
              )}

              {/* Sign Up Link */}
              <div style={{
                marginTop: "24px",
                textAlign: "center"
              }}>
                <p style={{
                  fontSize: "14px",
                  color: "rgb(148, 163, 184)",
                  margin: 0
                }}>
                  Don't have an account?{" "}
                  <a href="/signup" style={{
                    color: "#60a5fa",
                    textDecoration: "none",
                    fontWeight: "500"
                  }}>
                    Sign up
                  </a>
                </p>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div style={{
            marginTop: "32px",
            textAlign: "center",
            fontSize: "12px",
            color: "rgb(100, 116, 139)"
          }}>
            <p style={{ margin: 0 }}>
              © 2025 Briefcase AI. All rights reserved.
            </p>
          </div>
        </div>
      </main>

      {/* Add spinner animation */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />
    </div>
  );
}